type AppPhase = 'init' | 'interaction';
type RenderPhase = 'mount' | 'update';

interface RenderEntry {
  componentName: string;
  instanceId: string;
  renderPhase: RenderPhase;
  appPhase: AppPhase;
  changedProps?: string[];
  timestamp: number;
}

interface ComponentSummary {
  component: string;
  mounts: number;
  updates: number;
  initRenders: number;
  interactionRenders: number;
  total: number;
}

class RenderStore {
  private entries: RenderEntry[] = [];
  private _enabled: boolean | null = null;
  currentAppPhase: AppPhase = 'init';

  isEnabled(): boolean {
    if (this._enabled !== null) return this._enabled;
    try {
      const lsFlag = localStorage.getItem('DGUIDE_RENDER_DEBUG') === 'true';
      const urlFlag = new URLSearchParams(window.location.search).has('renderDebug');
      this._enabled = lsFlag || urlFlag;
    } catch {
      this._enabled = false;
    }
    return this._enabled;
  }

  setAppPhase(phase: AppPhase): void {
    if (!this.isEnabled()) return;
    this.currentAppPhase = phase;
    console.log(`%c[RenderTracker] App phase -> ${phase}`, 'color: #4CAF50; font-weight: bold');
  }

  recordRender(entry: Omit<RenderEntry, 'timestamp'>): void {
    if (!this.isEnabled()) return;
    this.entries.push({ ...entry, timestamp: performance.now() });
  }

  getSummary(): ComponentSummary[] {
    const map = new Map<string, ComponentSummary>();
    for (const e of this.entries) {
      let s = map.get(e.componentName);
      if (!s) {
        s = { component: e.componentName, mounts: 0, updates: 0, initRenders: 0, interactionRenders: 0, total: 0 };
        map.set(e.componentName, s);
      }
      if (e.renderPhase === 'mount') s.mounts++;
      else s.updates++;
      if (e.appPhase === 'init') s.initRenders++;
      else s.interactionRenders++;
      s.total++;
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }

  printSummary(): void {
    const summary = this.getSummary();
    if (summary.length === 0) {
      console.log('[RenderTracker] No renders recorded yet.');
      return;
    }
    console.log('%c[RenderTracker] Render Summary', 'color: #FF5722; font-weight: bold; font-size: 14px');
    console.table(summary);

    const totalRenders = summary.reduce((sum, s) => sum + s.total, 0);
    const totalInit = summary.reduce((sum, s) => sum + s.initRenders, 0);
    const totalInteraction = summary.reduce((sum, s) => sum + s.interactionRenders, 0);
    console.log(`%cTotal: ${totalRenders} renders (${totalInit} init, ${totalInteraction} interaction)`, 'color: #FF5722; font-weight: bold');
  }

  printDetailed(): void {
    if (!this.isEnabled()) return;
    const summary = this.getSummary();
    for (const s of summary) {
      const entries = this.entries.filter(e => e.componentName === s.component);
      const propsChanges = entries
        .filter(e => e.changedProps && e.changedProps.length > 0)
        .map(e => e.changedProps!.join(', '));
      console.groupCollapsed(
        `%c${s.component} %c(${s.total} renders: ${s.mounts} mounts, ${s.updates} updates)`,
        'color: #2196F3; font-weight: bold',
        'color: #666'
      );
      if (propsChanges.length > 0) {
        const freq = new Map<string, number>();
        for (const p of propsChanges) {
          freq.set(p, (freq.get(p) || 0) + 1);
        }
        console.log('Props that changed:', Object.fromEntries(freq));
      }
      console.log(`Init: ${s.initRenders}, Interaction: ${s.interactionRenders}`);
      console.groupEnd();
    }
  }

  getEntries(): RenderEntry[] {
    return [...this.entries];
  }

  reset(): void {
    this.entries = [];
    console.log('%c[RenderTracker] Counters reset', 'color: #4CAF50; font-weight: bold');
  }
}

export const renderStore = new RenderStore();

// Expose to window for easy console access
if (renderStore.isEnabled()) {
  (window as any).__renderTracker = renderStore;
  console.log(
    '%c[RenderTracker] Active! Use __renderTracker.printSummary() or __renderTracker.printDetailed()',
    'color: #4CAF50; font-weight: bold; font-size: 12px'
  );
}
